import {ExperimentNames} from '@wix/wix-events-commons-statics'
import {useEnvironment, useExperiments} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React from 'react'
import {useColors} from '../../../../hooks/colors'
import {usePageActions} from '../../../../hooks/state'
import {isPlaceArea, isPlaceInBasket, isPlaceReservableAsWhole} from '../../../../selectors/seating/place'
import {TicketProps} from './interfaces'
import {TicketAction} from './ticket-action'
import {OldTicketAction} from './ticket-action/old-index'
import {TicketInfo} from './ticket-info'
import {classes} from './ticket.st.css'
import {WholeElementReservation} from './whole-element-reservation'

export const Ticket = ({
  place,
  inDescription,
  showAccessibilityMode,
  onRemoveClick,
  onClick,
  onTicketMoreInfoClick,
  setForcedPopoverPlanPlaceId,
  basketView = false,
  chooseSeatMode,
  origin,
}: TicketProps) => {
  const inBasket = isPlaceInBasket(place)
  const area = isPlaceArea(place)
  const {darkColor} = useColors()
  const {isMobile} = useEnvironment()
  const wholeElementReservation = isPlaceReservableAsWhole(place)
  const {resetAreaCounterValue, selectPlace, unselectPlace} = usePageActions()

  const {experiments} = useExperiments()
  const planTicketsDesignEnabled = experiments.enabled(ExperimentNames.PlanTicketsDesign)

  const handleRemove: React.MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation()
    onRemoveClick?.(place)
    handleUnselectSeat()
  }

  const handleClick: React.MouseEventHandler<HTMLButtonElement | HTMLLIElement> = e => {
    e.stopPropagation()
    onClick?.(place)
  }

  const handleTicketMoreInfoClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation()
    resetAreaCounterValue({place})
    onTicketMoreInfoClick?.(place)
  }

  const handleSelectSeat = () => {
    setForcedPopoverPlanPlaceId?.(place.planPlaceId)

    if (planTicketsDesignEnabled && showAccessibilityMode && !inDescription) {
      selectPlace(place.planPlaceId)
    }
  }

  const handleUnselectSeat = () => {
    setForcedPopoverPlanPlaceId?.(null)

    if (planTicketsDesignEnabled && showAccessibilityMode && !inDescription) {
      unselectPlace()
    }
  }

  const onTicketClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    if (planTicketsDesignEnabled) {
      if (!inDescription && !basketView) {
        handleClick(event)
      }
    } else {
      if (!inDescription && !basketView && (!area || chooseSeatMode)) {
        handleClick(event)
      }
    }
  }

  return (
    <>
      <li
        className={classNames(classes.ticket, {
          [classes.selectedWired]: !inDescription && showAccessibilityMode && !basketView && inBasket && darkColor,
          [classes.selectedNotWired]: showAccessibilityMode && !basketView && inBasket && !darkColor,
          [classes.ticketHoverBorder]: !inDescription && !isMobile,
          [classes.ticketHoverCursor]: (!planTicketsDesignEnabled || !basketView) && !inDescription && !isMobile,
          [classes.ticketMultiBorder]: wholeElementReservation,
          [classes.mobile]: isMobile,
        })}
        onClick={onTicketClick}
        onMouseEnter={handleSelectSeat}
        onMouseLeave={handleUnselectSeat}
        onFocus={handleSelectSeat}
        onBlur={handleUnselectSeat}
        data-hook={`ticket-${place.id}`}
      >
        {wholeElementReservation ? (
          <div className={classNames(classes.multiBorder, {[classes.mobile]: isMobile})} />
        ) : null}
        <div className={classes.color} style={{backgroundColor: place.color}} />
        <TicketInfo
          inDescription={inDescription}
          basketView={basketView}
          place={place}
          onRemoveClick={onRemoveClick ? handleRemove : undefined}
          showAccessibilityMode={showAccessibilityMode}
        />
        {planTicketsDesignEnabled ? (
          <TicketAction
            place={place}
            inDescription={inDescription}
            onClick={handleClick}
            onTicketMoreInfoClick={handleTicketMoreInfoClick}
            basketView={basketView}
            showAccessibilityMode={showAccessibilityMode}
            origin={origin}
          />
        ) : (
          <>
            <OldTicketAction
              place={place}
              inDescription={inDescription}
              onClick={handleClick}
              onTicketMoreInfoClick={handleTicketMoreInfoClick}
              basketView={basketView}
              showAccessibilityMode={showAccessibilityMode}
              origin={origin}
            />
            {basketView || inDescription || showAccessibilityMode ? (
              <WholeElementReservation
                place={place}
                basketView={basketView}
                inDescription={inDescription}
                showAccessibilityMode={showAccessibilityMode}
              />
            ) : null}
          </>
        )}
      </li>
    </>
  )
}
